import { queryOptions } from "@tanstack/react-query";
import {
  ActivityQuery,
  AuthConfig,
  combineQueries,
  createRecordFromArray,
  getControlName,
  greaterThanToday,
  NonArchPlatform,
  Platform,
  parseDate,
  queries,
} from "@theprelude/core";
import {
  ExpectedResult,
  getExpectedResult,
  getPersonalPercentages,
  OptionalODPFields,
} from "@/lib/odp";
import { isValidControl } from "@/lib/validators";

export type DashboardThreat = {
  threatId: string;
  source_id?: string;
  source?: string;
  threat: string;
  author?: string;
  published?: string;
  protected?: number;
  benchmark?: number;
  status: boolean;
};

export const dashboardThreatsQuery = (
  authConfig: AuthConfig,
  query: ActivityQuery,
) =>
  queryOptions({
    queryKey: [authConfig, "dashboard-threats", query],
    queryFn: async (): Promise<DashboardThreat[]> => {
      const [account, threats, activity] = await combineQueries(
        queries.iam.getAccount(authConfig),
        queries.detect.threats(authConfig),
        queries.detect.activity.threats(authConfig, query),
      );

      const { queue } = account;
      const activityMap = createRecordFromArray(activity, "id");

      return threats
        .filter((t) => t.tombstoned === null)
        .map((threat) => {
          return {
            threatId: threat.id,
            source_id: threat.source_id ?? undefined,
            source: threat.source ?? undefined,
            threat: threat.name,
            author: threat.author ?? undefined,
            published: threat.published ?? undefined,
            protected: activityMap[threat.id]?.protected ?? undefined,
            benchmark: activityMap[threat.id]?.social ?? undefined,
            status: queue?.some((q) => q.threat === threat.id) ?? false,
          };
        });
    },
  });

export const dashboardNewThreatsQuery = (
  authConfig: AuthConfig,
  query: ActivityQuery,
) =>
  queryOptions({
    queryKey: [authConfig, "dashboard-new-threats", query],
    queryFn: async (): Promise<DashboardThreat[]> => {
      const [account, threats, activity] = await combineQueries(
        queries.iam.getAccount(authConfig),
        queries.detect.threats(authConfig),
        queries.detect.activity.threats(authConfig, query),
      );

      const { queue } = account;
      const activityMap = createRecordFromArray(activity, "id");

      return threats
        .filter((t) => t.tombstoned === null)
        .filter(
          (t) =>
            t.published && greaterThanToday(parseDate(t.created), 7) === false,
        )
        .map((threat) => {
          return {
            threatId: threat.id,
            source_id: threat.source_id ?? undefined,
            source: threat.source ?? undefined,
            threat: threat.name,
            author: threat.author ?? undefined,
            published: threat.published ?? undefined,
            protected: activityMap[threat.id]?.protected ?? undefined,
            benchmark: activityMap[threat.id]?.social ?? undefined,
            status: queue?.some((q) => q.threat === threat.id) ?? false,
          };
        });
    },
  });

export type DashboardTest = OptionalODPFields & {
  testId: string;
  name: string;
  account_id: string | undefined;
  technique?: string;
  threatsDisplay: { id: string; name: string }[];
  created?: string;
  supported_platforms: NonArchPlatform[];
  author: string | undefined;
  protected?: number;
  benchmark?: number;
  expectedResult?: ExpectedResult;
  status: boolean;
};

export const dashboardTestsQuery = (
  authConfig: AuthConfig,
  query: ActivityQuery,
) =>
  queryOptions({
    queryKey: [authConfig, "dashboard-tests", query],
    queryFn: async (): Promise<DashboardTest[]> => {
      const [account, tests, threats, testUsages] = await combineQueries(
        queries.iam.getAccount(authConfig),
        queries.detect.tests(authConfig),
        queries.detect.threats(authConfig),
        queries.detect.activity.tests(authConfig, query),
      );
      return tests
        .filter((t) => t.tombstoned === null)
        .map((test) => {
          const relevantThreatIds = threats
            .filter((threat) => threat.tests.includes(test.id))
            .map((threat) => {
              return { id: threat.id, name: threat.name };
            });

          const { queue } = account;
          const usage = testUsages.find((usage) => usage.id === test.id);

          return {
            testId: test.id,
            account_id: test.account_id ?? undefined,
            name: test.name,
            technique: test.technique ?? undefined,
            threatsDisplay: relevantThreatIds,
            created: test.created ?? undefined,
            supported_platforms: test.supported_platforms,
            author: test.author ?? undefined,
            protected: usage?.protected ?? undefined,
            ...getPersonalPercentages(usage?.personal),
            expectedResult: getExpectedResult(test.expected, usage?.personal),
            benchmark: usage?.social ?? undefined,
            status: queue?.some((q) => q.test === test.id) ?? false,
          };
        });
    },
  });

export type DashboardEndpoint = {
  endpoint_id: string;
  host: string;
  os?: string;
  dos?: Platform;
  control?: string;
  last_seen: string | null;
  tags: string[];
  policy: string | undefined;
  policy_name: string | undefined;
  performance?: number;
};

export const dashboardEndpointsQuery = (
  authConfig: AuthConfig,
  query: ActivityQuery,
) =>
  queryOptions({
    queryKey: [authConfig, "dashboard-endpoints", query],
    queryFn: async (): Promise<DashboardEndpoint[]> => {
      const [endpoints, endpointsActivity] = await combineQueries(
        queries.detect.endpoints(authConfig, 90),
        queries.detect.activity.endpoints(authConfig, query),
      );
      const endpointActivityById = createRecordFromArray(
        endpointsActivity,
        "id",
      );

      return endpoints.map((endpoint) => {
        return {
          endpoint_id: endpoint.endpoint_id,
          host: endpoint.host,
          os: endpoint.os ?? undefined,
          dos: endpoint.dos ?? undefined,
          control: isValidControl(endpoint.control)
            ? getControlName(endpoint.control)
            : undefined,
          last_seen: endpoint.last_seen,
          tags: endpoint.tags,
          policy: endpoint.policy ?? undefined,
          policy_name: endpoint.policy_name ?? undefined,
          performance:
            endpointActivityById[endpoint.endpoint_id]?.protected ?? undefined,
        };
      });
    },
  });

export const dashboardNewEndpointsQuery = (
  authConfig: AuthConfig,
  query: ActivityQuery,
) =>
  queryOptions({
    queryKey: [authConfig, "dashboard-new-endpoints", query],
    queryFn: async (): Promise<DashboardEndpoint[]> => {
      const [endpoints, endpointsActivity] = await combineQueries(
        queries.detect.endpoints(authConfig, 90),
        queries.detect.activity.endpoints(authConfig, query),
      );
      const endpointActivityById = createRecordFromArray(
        endpointsActivity,
        "id",
      );

      return endpoints
        .filter((e) => greaterThanToday(parseDate(e.created), 7) === false)
        .map((endpoint) => {
          return {
            endpoint_id: endpoint.endpoint_id,
            host: endpoint.host,
            os: endpoint.os ?? undefined,
            dos: endpoint.dos ?? undefined,
            control: isValidControl(endpoint.control)
              ? getControlName(endpoint.control)
              : undefined,
            last_seen: endpoint.last_seen,
            tags: endpoint.tags,
            policy: endpoint.policy ?? undefined,
            policy_name: endpoint.policy_name ?? undefined,
            performance:
              endpointActivityById[endpoint.endpoint_id]?.protected ??
              undefined,
          };
        });
    },
  });

export const dashboardActiveEndpointsQuery = (
  authConfig: AuthConfig,
  query: ActivityQuery,
) =>
  queryOptions({
    queryKey: [authConfig, "dashboard-active-endpoints", query],
    queryFn: async (): Promise<DashboardEndpoint[]> => {
      const [endpoints, endpointsActivity] = await combineQueries(
        queries.detect.endpoints(authConfig, 90),
        queries.detect.activity.endpoints(authConfig, query),
      );
      const endpointActivityById = createRecordFromArray(
        endpointsActivity,
        "id",
      );

      return endpoints
        .filter(
          (e) =>
            e.last_seen &&
            greaterThanToday(parseDate(e.last_seen), 1) === false,
        )
        .map((endpoint) => {
          return {
            endpoint_id: endpoint.endpoint_id,
            host: endpoint.host,
            os: endpoint.os ?? undefined,
            dos: endpoint.dos ?? undefined,
            control: isValidControl(endpoint.control)
              ? getControlName(endpoint.control)
              : undefined,
            last_seen: endpoint.last_seen,
            tags: endpoint.tags,
            policy: endpoint.policy ?? undefined,
            policy_name: endpoint.policy_name ?? undefined,
            performance:
              endpointActivityById[endpoint.endpoint_id]?.protected ??
              undefined,
          };
        });
    },
  });
